
// API adapter to work with Supabase
import { supabase } from "@/integrations/supabase/client";
import { OnboardingState } from "@/lib/types";

export async function createProfile(
  email: string, 
  name?: string | null, 
  whatsapp?: string | null,
  username?: string | null,
  progress?: OnboardingState | null
) {
  try {
    console.log("createProfile function called with:", { 
      email, 
      name, 
      whatsapp, 
      username,
      progress: progress ? `Day ${progress.currentDayIndex + 1}, Step ${progress.currentSubStepIndex + 1}` : null
    });
    
    if (!email) {
      throw new Error("Email is required");
    }

    // Check if user exists
    const { data: existingUser, error: fetchError } = await supabase
      .from('users')
      .select('*')
      .eq('email', email)
      .maybeSingle();
      
    if (fetchError) {
      console.error("Error checking if user exists:", fetchError);
      throw fetchError;
    }
    
    if (existingUser) {
      console.log("User exists, updating:", existingUser);
      
      // Only update fields that are provided
      const updates: Record<string, any> = {};
      
      if (name !== undefined) updates.fullname = name;
      if (whatsapp !== undefined) updates.whatsapp = whatsapp;
      if (username !== undefined) updates.tiktokusername = username;
      if (progress !== undefined && progress !== null) updates.savedstep = JSON.stringify(progress);
      
      // Skip update if no fields need to be updated
      if (Object.keys(updates).length === 0) {
        console.log("No fields to update, returning existing user");
        return { data: existingUser };
      }
      
      const { data, error: updateError } = await supabase
        .from('users')
        .update(updates)
        .eq('email', email)
        .select()
        .single();
        
      if (updateError) {
        console.error("Error updating user:", updateError);
        throw updateError;
      }
      
      console.log("User updated successfully:", data);
      return { data };
    }
    
    // Create new profile
    const profileData = {
      email,
      fullname: name || null,
      whatsapp: whatsapp || null,
      tiktokusername: username || null,
      savedstep: progress ? JSON.stringify(progress) : null
    };
    
    console.log("Creating new user:", profileData);
    
    const { data, error: insertError } = await supabase
      .from('users')
      .insert(profileData)
      .select()
      .single();
      
    if (insertError) {
      console.error("Error creating user:", insertError);
      throw insertError;
    }
    
    console.log("User created successfully:", data);
    return { data };
  } catch (error) {
    console.error("Error in createProfile adapter:", error);
    throw error;
  }
}

export async function saveOnboardingProgress(email: string, progress: OnboardingState): Promise<boolean> {
  try {
    if (!email || !progress) {
      console.error("Missing required parameters to save progress");
      return false;
    }
    
    console.log(`Saving onboarding progress for ${email}: Day ${progress.currentDayIndex + 1}, SubStep ${progress.currentSubStepIndex + 1}`);
    
    const { error } = await supabase
      .from('users')
      .update({
        savedstep: JSON.stringify(progress),
        updated_at: new Date().toISOString()
      })
      .eq('email', email);
      
    if (error) {
      console.error("Error saving progress to database:", error);
      return false;
    }
    
    return true;
  } catch (error) {
    console.error("Error in saveOnboardingProgress:", error);
    return false;
  }
}

export async function getUserOnboardingProgress(email: string): Promise<{ exists: boolean, progress: OnboardingState | null }> {
  try {
    console.log(`Retrieving progress for ${email}`);
    
    const { data, error } = await supabase
      .from('users')
      .select('savedstep')
      .eq('email', email)
      .maybeSingle();
      
    if (error) {
      console.error("Error retrieving user progress:", error);
      return { exists: false, progress: null };
    }
    
    if (!data) {
      return { exists: false, progress: null };
    }
    
    let progress = null;
    
    if (data.savedstep) {
      try {
        progress = JSON.parse(data.savedstep);
        console.log(`Retrieved progress: Day ${progress.currentDayIndex + 1}, SubStep ${progress.currentSubStepIndex + 1}`);
      } catch (parseError) {
        console.error("Error parsing saved progress:", parseError);
      }
    }
    
    return { exists: true, progress };
  } catch (error) {
    console.error("Error in getUserOnboardingProgress:", error);
    return { exists: false, progress: null };
  }
}
